<template>
  <form
    id="icon-family-style"
    action=""
    class="icon-details-styling"
    @submit.prevent
  >
    <div
      v-if="!isNotShowingFamilyAndStyles"
      class="margin-bottom-xl"
      style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md)"
    >
      <div
        v-if="version !== 5"
        style=""
      >
        <wa-select-custom
          :value="styling.family"
          :options="iconFamilies"
          :custom-style="selectCustomStyle"
          :custom-select-style="selectCustomSelectStyle"
          icon-name="faSwatchbook"
          is-minimal
          label="Icon Family"
          @change="(value) => change('family', value)"
        />
      </div>
      <div style="">
        <span
          id="icon_style_label"
          class="sr-only"
          >Select an Icon Style</span
        >
        <div
          id="icon_style"
          aria-labelledby="icon_style_label"
          class="buttons select-icon-style"
          style="display: grid; grid-template-columns: 1fr 1fr 1fr 1fr"
        >
          <button
            v-for="style in iconStyles(styling.family)"
            :key="style.label"
            :aria-label="style.label"
            :style="styleSelectButtonStyle(style.value)"
            :class="['select-icon-style-button margin-top-0 compact', { active: style.value == styling.style }]"
            data-balloon-pos="up"
            @click.prevent="() => change('style', style.value)"
          >
            <i :class="[`${styleSelectStyle(style.value)} fa-${icon.name} fa-xl`]" />
          </button>
          <span
            v-if="styling.family === 'duotone'"
            class="display-flex align-center align-middle"
            style="
              grid-column: 2 / 5;
              background-color: var(--fa-lt-gravy);
              border-radius: 0 var(--button-border-radius) var(--button-border-radius) 0;
              cursor: default;
            "
            data-balloon-pos="up"
            aria-label="More Duotone styles coming!"
          >
            <font-awesome-icon
              :icon="$v6.solid.faEllipsis"
              size="xl"
              fixed-width
              style="color: var(--fa-gravy)"
            />
          </span>
          <span
            v-if="styling.family === 'sharp-duotone'"
            class="display-flex align-center align-middle"
            style="
              grid-column: 2 / 5;
              background-color: var(--fa-lt-gravy);
              border-radius: 0 var(--button-border-radius) var(--button-border-radius) 0;
              cursor: default;
            "
            data-balloon-pos="up"
            aria-label="More Sharp Duotone styles coming!"
          >
            <font-awesome-icon
              :icon="$v6.solid.faEllipsis"
              size="xl"
              fixed-width
              style="color: var(--fa-gravy)"
            />
          </span>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
  import { styleOptions, getLongCode, getFamilyDefaultStyle, getStyleOrder } from 'app/releases.js'
  import { startCase } from 'lodash'

  export default {
    name: 'IconFamilyStyle',

    props: {
      icon: {
        type: Object
      },
      defaultStyles: {
        type: Object
      },
      styling: {
        type: Object,
        required: true
      },
      version: {
        type: Number
      }
    },

    computed: {
      isNotShowingFamilyAndStyles() {
        return this.styling.family === 'brands' || this.styling.style === 'custom'
      },

      iconName() {
        if (this.styling.style === 'duotone') {
          return 'faCircleHalf'
        } else {
          return 'faCircle'
        }
      },

      iconStyle() {
        if (this.iconName === 'faCircle' || this.iconName === 'faCircleHalf') {
          return this.styling.style
        }

        return 'regular'
      },

      iconFamilies() {
        const families = this.icon.membership.pro.reduce((acc, membership) => {
          if (!acc.some((item) => item.value === membership.family)) {
            acc.push({ name: startCase(membership.family.replace('-', ' ')), value: membership.family })
          }

          return acc
        }, [])

        return families.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0))
      },

      selectCustomStyle() {
        return {
          '--input-with-icon-color': 'var(--text-color)'
        }
      },

      selectCustomSelectStyle() {
        return {
          '--input-border-color': 'var(--fa-gravy)',
          'font-weight': 'var(--font-weight-normal)',
          'padding-top': 'var(--spacing-sm)',
          'padding-bottom': 'var(--spacing-sm)'
        }
      }
    },

    methods: {
      iconStyles(family) {
        const styles = this.icon.membership.pro.reduce((acc, membership) => {
          if (membership.family === family && !acc.some((item) => item.value === membership.style)) {
            acc.push({
              label: startCase(membership.style.replace('-', ' ')),
              value: membership.style,
              order: getStyleOrder(this.version, family, membership.style)
            })
          }

          return acc
        }, [])

        return styles.sort((a, b) => (a.order > b.order ? 1 : -1))
      },

      change(attr, value) {
        this.$posthog.capture('icon_preview_styling_update', {
          attribute: attr,
          value: value,
          icon_name: this.icon.name,
          icon_style: this.styling.style,
          icon_family: this.styling.family
        })

        if (attr === 'family' && !this.familyContainStyle(value, this.styling.style)) {
          return this.$emit('change', { ...this.styling, ...{ [attr]: value, style: getFamilyDefaultStyle(value) } })
        } else {
          return this.$emit('change', { ...this.styling, ...{ [attr]: value } })
        }
      },

      familyContainStyle(family, style) {
        const styles = styleOptions(this.version, family)
        return styles.some((s) => s.value === style)
      },

      styleSelectStyle(style) {
        if (this.version === 5) {
          return `fa${style.charAt(0)}`
        } else {
          return getLongCode(this.styling.family, style ? style : 'solid')
        }
      },

      styleSelectButtonStyle(iconStyle) {
        let style = {}

        if (iconStyle === this.styling.style) {
          style['--button-hover-color'] = 'var(--text-color)'
          style['--button-hover-background'] = 'var(--fa-gravy)'
        } else {
          style['--button-hover-color'] = 'var(--link-hover-color)'
          style['--button-hover-background'] = 'transparent'
        }

        return {
          ...style,
          ...{
            '--button-border-radius': 'var(--input-border-radius)',
            '--button-border-color': 'var(--fa-gravy)',
            '--button-box-shadow': 'none',
            '--button-box-shadow-width': '0',
            '--button-padding': 'var(--spacing-sm) var(--spacing-lg)',
            '--button-color': 'var(--link-color)',
            '--button-active-border-color': 'var(--fa-gravy)',
            '--button-active-background': 'var(--fa-gravy)',
            '--button-active-color': 'var(--text-color)'
          }
        }
      }
    }
  }
</script>
